import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { ModalActionButtonStyles } from "../../Common/styles/modal";
import { useTranslation } from "react-i18next";
import StudyCodeRegistration, {
  AvailabilityState,
} from "../StudyCodeRegistration";
import { deriveStudyCodeInitials } from "../../../utils/studyCodeUtils";

type Props = {
  data?: any;
  handleNext: () => void;
  setStudyId: (value: string) => void;
};

const STUDY_TYPE_OPTIONS = [
  {
    labelKey: "ADD_STUDY_DRAWER.STUDY_TYPE_OBSERVATIONAL",
    value: "observational_study",
  },
  {
    labelKey: "ADD_STUDY_DRAWER.STUDY_TYPE_INTERVENTIONAL",
    value: "interventional_study",
  },
  {
    labelKey: "ADD_STUDY_DRAWER.STUDY_TYPE_PATIENT_REGISTRY",
    value: "patient_registry",
  },
];

const schema = yup.object().shape({
  name: yup
    .string()
    .required("ADD_STUDY_DRAWER.VALIDATION_STUDY_NAME_REQUIRED")
    .min(4, "ADD_STUDY_DRAWER.VALIDATION_STUDY_NAME_MIN_LENGTH"),
  type: yup.string().required("ADD_STUDY_DRAWER.VALIDATION_STUDY_TYPE_REQUIRED"),
  studyCode: yup
    .string()
    .required("ADD_STUDY_DRAWER.VALIDATION_STUDY_CODE_REQUIRED"),
  siteName: yup.string().required("ADD_STUDY_DRAWER.VALIDATION_SITE_NAME_REQUIRED"),
  siteAbbreviation: yup
    .string()
    .required("ADD_STUDY_DRAWER.VALIDATION_SITE_ABBREVIATION_REQUIRED"),
  countryId: yup.string().required("ADD_STUDY_DRAWER.VALIDATION_COUNTRY_REQUIRED"),
});

const GeneralAndSiteInfo = ({ data, handleNext, setStudyId }: Props) => {
  const { t } = useTranslation("studies");
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [countriesList, setCountriesList] = useState<any[]>([]);
  const [availability, setAvailability] = useState<AvailabilityState>("idle");
  const [checkedCode, setCheckedCode] = useState("");
  const studyCodeTouchedRef = useRef(false);

  const handleAvailabilityChange = useCallback(
    (state: AvailabilityState, checkedValue: string) => {
      setAvailability(state);
      setCheckedCode(checkedValue);
    },
    [],
  );

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(`/data/countries`);
        const newData = res?.data?.data?.map((country: any) => ({
          value: country?.valueId,
          label: country?.label,
        }));
        setCountriesList(newData);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        name: values?.name,
        type: values?.type,
        studyCode: values?.studyCode,
        site: {
          name: values?.siteName,
          abbreviation: values?.siteAbbreviation,
          countryId: values?.countryId,
        },
      };
      let res: AxiosResponse;
      if (data?.id) {
        res = await http.patch(`/study/${data?.id}`, body);
      } else {
        res = await http.post(`/study`, body);
      }
      setStudyId(res?.data?.data?.id);
      toastMessage("success", res?.data?.message);
      setSubmitLoader(false);
      handleNext();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" color="text.secondary">
        {t("ADD_STUDY_DRAWER.GENERAL_AND_SITE_DESCRIPTION")}
      </Typography>
      <Divider sx={{ my: 2.5 }} />
      {!loading ? (
        <Formik
          initialValues={{
            name: data?.studyName || "",
            type: data?.type || "observational_study",
            studyCode: "",
            siteName: data?.siteName || "",
            siteAbbreviation: data?.abbreviation || "",
            countryId: data?.countryId || "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values, isValid }) => {
            const studyCodeReady =
              availability === "available" &&
              checkedCode.length > 0 &&
              checkedCode === values.studyCode.trim();

            return (
              <Form>
                <Typography
                  variant="h3"
                  fontWeight={"regular"}
                  color="text.primary"
                  mb={2.5}
                >
                  {t("ADD_STUDY_DRAWER.GENERAL_INFORMATION")}
                </Typography>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="study-name">
                    {t("ADD_STUDY_DRAWER.STUDY_NAME_LABEL")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder={t("ADD_STUDY_DRAWER.STUDY_NAME_PLACEHOLDER")}
                    id="study-name"
                    {...getFieldProps("name")}
                    onChange={(e) => {
                      const nextName = e.target.value;
                      setFieldValue("name", nextName);
                      if (!studyCodeTouchedRef.current) {
                        setFieldValue(
                          "studyCode",
                          deriveStudyCodeInitials(nextName),
                        );
                      }
                    }}
                    error={touched?.name && errors?.name ? true : false}
                    helperText={
                      touched?.name && errors?.name
                        ? t(errors?.name as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="study-type">
                    {t("ADD_STUDY_DRAWER.STUDY_TYPE_LABEL")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    value={values.type}
                    onChange={(e) => {
                      setFieldValue("type", e.target.value);
                    }}
                    fullWidth
                    id="study-type"
                  >
                    {STUDY_TYPE_OPTIONS.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={touched?.type && errors?.type ? true : false}
                  >
                    {touched?.type && errors?.type
                      ? t(errors?.type as string)
                      : " "}
                  </FormHelperText>
                </FormControl>

                <StudyCodeRegistration
                  mode="embedded"
                  value={values.studyCode}
                  onChange={(code) => {
                    studyCodeTouchedRef.current = true;
                    setFieldValue("studyCode", code);
                  }}
                  onAvailabilityChange={handleAvailabilityChange}
                />

                <Divider sx={{ my: 2.5 }} />

                <Typography
                  variant="h3"
                  fontWeight={"regular"}
                  color="text.primary"
                  mb={2.5}
                >
                  {t("ADD_STUDY_DRAWER.SITE_INFORMATION")}
                </Typography>

                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="site-name">
                    {t("ADD_STUDY_DRAWER.SITE_NAME_LABEL")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder={t("ADD_STUDY_DRAWER.SITE_NAME_PLACEHOLDER")}
                    id="site-name"
                    {...getFieldProps("siteName")}
                    error={touched?.siteName && errors?.siteName ? true : false}
                    helperText={
                      touched?.siteName && errors?.siteName
                        ? t(errors?.siteName as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="country-id">
                    {t("ADD_STUDY_DRAWER.COUNTRY_LABEL")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    value={values.countryId}
                    onChange={(e) => {
                      setFieldValue("countryId", e.target.value);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: "300px", overflowY: "auto" },
                      },
                    }}
                    fullWidth
                    id="country-id"
                  >
                    {countriesList.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={touched?.countryId && errors?.countryId ? true : false}
                  >
                    {touched?.countryId && errors?.countryId
                      ? t(errors?.countryId as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="site-abbreviation">
                    {t("ADD_STUDY_DRAWER.SITE_ABBREVIATION_LABEL")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder={t(
                      "ADD_STUDY_DRAWER.SITE_ABBREVIATION_PLACEHOLDER",
                    )}
                    id="site-abbreviation"
                    {...getFieldProps("siteAbbreviation")}
                    error={
                      touched?.siteAbbreviation && errors?.siteAbbreviation
                        ? true
                        : false
                    }
                    helperText={
                      touched?.siteAbbreviation && errors?.siteAbbreviation
                        ? t(errors?.siteAbbreviation as string)
                        : " "
                    }
                    inputProps={{ maxLength: 6 }}
                  />
                </FormControl>
                <Box sx={ModalActionButtonStyles}>
                  {!submitLoader ? (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!isValid || !studyCodeReady}
                    >
                      {t("ADD_STUDY_DRAWER.NEXT_STEP")}
                    </Button>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <CircularProgress size={25} />
        </Box>
      )}
    </Box>
  );
};

export default GeneralAndSiteInfo;
